import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import { CountUp } from "@/components/animations";
import {
    History, ChevronRight, ChevronLeft, TrendingUp, TrendingDown, Minus, Award, Sparkles, AlertCircle, Plus,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { tierMeta } from "@/lib/tiers";


function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return "—";
    }
}

function HistoryRow({ entry, index, prevScore }) {
    const tier = tierMeta(entry.tier);
    const delta = prevScore != null ? entry.score - prevScore : 0;
    const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
    const deltaColor = delta > 0 ? "text-green-pioneer" : delta < 0 ? "text-red-starter" : "text-text-secondary";

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, ease: EASING.standard, duration: DURATION.sectionReveal }}
        >
            <Link
                to={`/genz-credit-scoring/history/${entry.id}`}
                className="block group"
                data-testid={`history-row-${entry.id}`}
            >
                <GlassCard hover className={`p-5 border ${tier.border}`}>
                    <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${tier.bg} ${tier.border} border shrink-0`}>
                            <span className="font-mono font-bold text-lg" style={{ color: tier.color }}>
                                {entry.score}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-sm font-semibold ${tier.text}`}>{entry.tier || "—"}</span>
                                {prevScore != null && delta !== 0 && (
                                    <span className={`text-xs flex items-center gap-1 ${deltaColor}`}>
                                        <DeltaIcon size={12} /> {delta > 0 ? "+" : ""}{delta}
                                    </span>
                                )}
                            </div>
                            <p className="text-text-secondary text-xs">
                                {formatDate(entry.created_at)}
                            </p>
                            <p className="text-text-secondary text-xs mt-1">
                                {Math.round((entry.confidence || 0) * 100)}% confidence ·{" "}
                                {entry.evidence_count ?? entry.evidence_snapshot?.length ?? 0} evidence sources
                            </p>
                        </div>
                        <ChevronRight className="text-text-secondary group-hover:text-text-primary transition-colors shrink-0" size={20} />
                    </div>
                </GlassCard>
            </Link>
        </motion.div>
    );
}

export default function ScoreHistory() {
    const [scores, setScores] = useState([]);
    const [pageMeta, setPageMeta] = useState({ page: 1, total: 0, total_pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/api/score/history", { params: { page, page_size: 20 } });
                if (cancelled) return;
                setScores(data.scores || []);
                setPageMeta({
                    page: data.page || page,
                    total: data.total ?? data.scores?.length ?? 0,
                    total_pages: data.total_pages || 1,
                });
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.detail || "Couldn't load your score history.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [page]);

    // Scores arrive newest-first. Use this for display, but compute deltas vs the
    // chronologically previous one (i.e. the next entry in the sorted-desc list).
    const summary = useMemo(() => {
        if (scores.length === 0) return null;
        const latest = scores[0];
        const earliest = scores[scores.length - 1];
        const avgConfidence = scores.reduce((acc, s) => acc + (s.confidence || 0), 0) / scores.length;
        return {
            count: pageMeta.total || scores.length,
            latestScore: latest.score,
            earliestScore: earliest.score,
            netChange: latest.score - earliest.score,
            avgConfidence,
        };
    }, [scores, pageMeta.total]);

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh">
                <p className="text-text-secondary">Loading your history...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh">
                <GlassCard className="p-8 max-w-md text-center">
                    <AlertCircle size={32} className="text-red-starter mx-auto mb-3" />
                    <h2 className="font-heading text-xl font-semibold text-text-primary mb-2">Couldn't load history</h2>
                    <p className="text-text-secondary text-sm">{error}</p>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="pt-24 pb-20 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="score-history-page">
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="max-w-4xl mx-auto"
            >
                <div className="flex items-start justify-between gap-4 mb-8">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-text-secondary font-semibold mb-3 flex items-center gap-2">
                            <History size={14} /> My Score History
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-text-primary mb-2">
                            Your scoring journey
                        </h1>
                        <p className="text-text-secondary text-base">
                            Every score you've generated, with full submission data preserved.
                        </p>
                    </div>
                    <Link
                        to="/genz-credit-scoring/flow"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-primary text-white text-sm font-medium hover:bg-blue-primary/90 transition-all shrink-0"
                    >
                        <Plus size={14} /> Generate New Score
                    </Link>
                </div>

                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <GlassCard className="p-4">
                            <p className="text-text-secondary text-xs mb-1.5 flex items-center gap-1.5">
                                <History size={12} /> Total Scores
                            </p>
                            <p className="font-mono text-2xl font-bold text-text-primary">
                                <CountUp target={summary.count} duration={0.9} />
                            </p>
                        </GlassCard>
                        <GlassCard className="p-4">
                            <p className="text-text-secondary text-xs mb-1.5 flex items-center gap-1.5">
                                <Sparkles size={12} /> Latest Score
                            </p>
                            <p className="font-mono text-2xl font-bold" style={{ color: tierMeta(scores[0].tier).color }}>
                                <CountUp target={summary.latestScore} duration={1.1} />
                            </p>
                        </GlassCard>
                        <GlassCard className="p-4">
                            <p className="text-text-secondary text-xs mb-1.5 flex items-center gap-1.5">
                                <TrendingUp size={12} /> Net Change
                            </p>
                            <p className={`font-mono text-2xl font-bold ${
                                summary.netChange > 0 ? "text-green-pioneer" : summary.netChange < 0 ? "text-red-starter" : "text-text-primary"
                            }`}>
                                {summary.netChange > 0 ? "+" : ""}{summary.netChange}
                            </p>
                        </GlassCard>
                        <GlassCard className="p-4">
                            <p className="text-text-secondary text-xs mb-1.5 flex items-center gap-1.5">
                                <Award size={12} /> Avg Confidence
                            </p>
                            <p className="font-mono text-2xl font-bold text-text-primary">
                                {Math.round(summary.avgConfidence * 100)}%
                            </p>
                        </GlassCard>
                    </div>
                )}

                {scores.length === 0 ? (
                    <GlassCard className="p-10 text-center">
                        <History size={40} className="text-text-secondary mx-auto mb-4 opacity-50" />
                        <h2 className="font-heading text-2xl font-semibold text-text-primary mb-2">
                            No scores yet
                        </h2>
                        <p className="text-text-secondary mb-6 max-w-md mx-auto">
                            Generate your first score to start your scoring journey. Every score you create will be preserved here with the full submission data.
                        </p>
                        <Link
                            to="/genz-credit-scoring/flow"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-primary text-white font-medium hover:bg-blue-primary/90 transition-all"
                        >
                            <Plus size={16} /> Generate My First Score
                        </Link>
                    </GlassCard>
                ) : (
                    <div className="space-y-3">
                        {scores.map((entry, idx) => (
                            <HistoryRow
                                key={entry.id}
                                entry={entry}
                                index={idx}
                                prevScore={scores[idx + 1]?.score}
                            />
                        ))}
                        {pageMeta.total_pages > 1 && (
                            <div className="flex items-center justify-between pt-5">
                                <p className="text-xs text-text-secondary">
                                    Page {pageMeta.page} of {pageMeta.total_pages} · {pageMeta.total} total
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={pageMeta.page <= 1}
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/10 text-text-secondary text-xs hover:text-text-primary hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft size={12} /> Prev
                                    </button>
                                    <button
                                        onClick={() => setPage((p) => Math.min(pageMeta.total_pages, p + 1))}
                                        disabled={pageMeta.page >= pageMeta.total_pages}
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-white/10 text-text-secondary text-xs hover:text-text-primary hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Next <ChevronRight size={12} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        </div>
    );
}
